import { computed, ref, SetupContext, watch } from 'vue'

import Time from '@web/common/Time'
import { Dayjs } from 'dayjs'
import i18n from '@web/plugins/i18n'

export interface Props {
  modelValue: string | number;
  error: string;
}

export const props = {
  modelValue: {
    type: String,
    default: null
  },
  error: {
    type: String,
    default: ''
  }
}

export const emits = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  'update:modelValue': (payload: string): boolean => true
}

function useDateSelect (props: Props, emit: SetupContext<typeof emits>['emit']) {
  const minYear = 10
  const maxYear = 70
  const date = ref<Dayjs>(Time().add(-minYear, 'y'))
  const d = ref(date.value.date().toString())
  const m = ref(date.value.month().toString())
  const y = ref(date.value.year().toString())
  const daysInMonth = computed(() => {
    const _daysInMonth: { label: string, value: string }[] = []
    for (let i = 1; i <= date.value.daysInMonth(); i++) {
      _daysInMonth.push({
        label: i.toString(),
        value: i.toString()
      })
    }
    return _daysInMonth
  })
  const allMonths = [
    {
      label: i18n.global.t('monthNames.january'),
      value: '0'
    },
    {
      label: i18n.global.t('monthNames.february'),
      value: '1'
    },
    {
      label: i18n.global.t('monthNames.march'),
      value: '2'
    },
    {
      label: i18n.global.t('monthNames.april'),
      value: '3'
    },
    {
      label: i18n.global.t('monthNames.may'),
      value: '4'
    },
    {
      label: i18n.global.t('monthNames.june'),
      value: '5'
    },
    {
      label: i18n.global.t('monthNames.july'),
      value: '6'
    },
    {
      label: i18n.global.t('monthNames.august'),
      value: '7'
    },
    {
      label: i18n.global.t('monthNames.september'),
      value: '8'
    },
    {
      label: i18n.global.t('monthNames.october'),
      value: '9'
    },
    {
      label: i18n.global.t('monthNames.november'),
      value: '10'
    },
    {
      label: i18n.global.t('monthNames.december'),
      value: '11'
    }
  ]
  const allYears: { label: string, value: string }[] = []
  for (let i = Time().year() - maxYear; i <= Time().year() - minYear; i++) {
    allYears.push({
      label: i.toString(),
      value: i.toString()
    })
  }
  allYears.reverse()

  watch(() => props.modelValue, () => {
    const _date = Time(props.modelValue, 'DD.MM.YYYY')
    if (_date.isValid()) {
      date.value = _date
      d.value = _date.date().toString()
      m.value = _date.month().toString()
      y.value = _date.year().toString()
    }
  }, {
    immediate: true
  })

  watch(d, (newDate) => {
    const _date = date.value.date(Number(newDate)).valueOf()
    emit('update:modelValue', Time(_date).format('DD.MM.YYYY'))
  })
  watch(m, (newMonth) => {
    const _date = date.value.month(Number(newMonth))
    emit('update:modelValue', Time(_date).format('DD.MM.YYYY'))
  })
  watch(y, (newYear) => {
    const _date = date.value.year(Number(newYear)).valueOf()
    emit('update:modelValue', Time(_date).format('DD.MM.YYYY'))
  })

  return {
    date,
    daysInMonth,
    allMonths,
    allYears,
    d,
    m,
    y
  }
}

export default useDateSelect
